import { useState, useEffect, useRef } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import styled, { keyframes } from "styled-components";
import SVG from "react-inlinesvg";
import Auth from "src/Auth";
import Utils from "src/Utils";
import { useToast } from "src/components/ToastWrapper/index.js";
import { SudoButton } from "src/sudokit/components/SudoButton";
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid #ccc;
  border-top: 2px solid #333;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const Container = styled.div``;

const StreamContainer = styled.div`
  padding: 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  max-height: 500px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  height: 500px;
  background-color: #f9fafb;
  font-family: ui-monospace, 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
  font-size: 0.875rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  flex-direction: row;
  gap: 0.75rem;
`;

const ErrorContainer = styled.div`
  color: #dc2626;
  background-color: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
`;

const ChunkLogContainer = styled.div`
  padding: 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  margin-top: 1.5rem;
  background-color: white;
`;

const ChunkLogHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;

  h3 {
    font-size: 1rem;
    font-weight: 600;
    color: #374151;
    margin: 0;
  }

  button {
    background: none !important;
    border: none !important;
    cursor: pointer !important;
    padding: 0.25rem !important;
    margin: 0 !important;
    height: 24px !important;
    width: 24px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    border-radius: 0.25rem !important;
    transition: background-color 0.2s;

    &:hover {
      background-color: #f3f4f6 !important;
    }

    svg {
      path {
        fill: #374151 !important;
      }
    }
  }
`;

const RawLogContainer = styled.div`
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  white-space: pre-wrap;
  word-wrap: break-word;
  height: 500px;
  overflow-y: auto;
  background-color: #f9fafb;
  font-family: ui-monospace, 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
  font-size: 0.875rem;
  color: #374151;
`;

function StreamReplay({ invocationId }) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [streamedContent, setStreamedContent] = useState("");
  const [chunkLogAvailable, setChunkLogAvailable] = useState(false);
  const [chunkLog, setChunkLog] = useState(null);
  const [mode, setMode] = useState("");
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(true);
  const { setToast } = useToast();
  const streamContainerRef = useRef(null);

  useEffect(() => {
    if (streamContainerRef.current) {
      const container = streamContainerRef.current;
      container.scrollTop = container.scrollHeight;
    }
  }, [streamedContent]);

  useEffect(() => {
    if (!invocationId) return;

    const checkAvailability = async () => {
      try {
        const response = await Auth.fetchWithIdToken("/api/admin/streamTools/replay-available-invocations", {
          invocationId,
        });

        setChunkLogAvailable(response.chunkLogAvailable);
        setChunkLog(response.chunkLog);
      } finally {
        setIsCheckingAvailability(false);
      }
    };
    checkAvailability();
  }, [invocationId]);

  const runReplay = async () => {
    setStreamedContent("");
    setIsLoading(true);
    await Auth.streamWithIdToken(
      `/api/admin/streamTools/replay-chunk-log`,
      {
        method: "POST",
        invocationId,
      },
      null,
      (chunk) => {
        setMode(chunk.mode);
        if (chunk.mode === "writing") {
          setStreamedContent((prev) => prev + chunk.text);
        }
      },
      () => {
        setIsLoading(false);
        setMode("completed");
      },
      (error) => {
        setIsLoading(false);
        setError(error);
        setMode("error");
      }
    );
  };

  const reset = () => {
    setStreamedContent("");
    setIsLoading(false);
    setError(null);
    setMode("");
  };

  if (isCheckingAvailability) {
    return (
      <Container>
        <div className="flex items-center gap-3 text-gray-600">
          <Spinner />
          <span>Checking chunk log availability...</span>
        </div>
      </Container>
    );
  }

  if (!chunkLogAvailable) {
    return (
      <Container>
        <div className="text-center py-8 text-gray-500">
          Chunk log replay not available
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <ButtonContainer>
        <SudoButton
          variant="primary"
          size="small"
          onClick={runReplay}
          disabled={isLoading}
        >
          Run Stream Replay
        </SudoButton>
        <SudoButton
          variant="tertiary"
          size="small"
          onClick={reset}
          disabled={isLoading}
        >
          Reset
        </SudoButton>
        {isLoading && <Spinner />}
        {mode && (
          <div className="text-sm text-gray-600">
            <span className="font-medium">Mode:</span> <span className="text-gray-900">{mode.toUpperCase()}</span>
          </div>
        )}
      </ButtonContainer>

      {error && (
        <ErrorContainer>
          <strong>Error:</strong>
          <pre className="mt-2 text-xs overflow-auto">{JSON.stringify(error, null, 2)}</pre>
        </ErrorContainer>
      )}
      <StreamContainer ref={streamContainerRef}>
        {streamedContent ? Utils.nl2br(streamedContent) : (
          <div className="text-gray-400 text-center">Stream output will appear here...</div>
        )}
      </StreamContainer>

      <ChunkLogContainer>
        <ChunkLogHeader>
          <h3>Original Raw Chunk Log</h3>
          {chunkLog?.log && (
            <CopyToClipboard
              text={chunkLog.log}
              options={{ format: "text/plain" }}
              onCopy={() => {
                setToast("Copied chunk log to clipboard");
              }}
            >
              <button title="Copy chunk log">
                <SVG
                  height={20}
                  width={20}
                  src="/assets/Copy.svg"
                />
              </button>
            </CopyToClipboard>
          )}
        </ChunkLogHeader>
        {chunkLog?.log ? (
          <RawLogContainer>{Utils.nl2br(chunkLog.log)}</RawLogContainer>
        ) : (
          <div className="text-center py-8 text-gray-400">No chunk log data available</div>
        )}
      </ChunkLogContainer>
    </Container>
  );
}

export default StreamReplay;
